import { useState, useEffect, useRef } from 'react';
import { fetchSiteSettings, updateSiteFooter, fetchSiteTitle, updateSiteTitle, fetchAdminSettings, fetchTiers, updateAdminSettings, fetchSiteFreeTier, fetchContactSettings, updateContactSettings, fetchBadges, updateBadgeDefinition } from '../services/adminApi';
import AdminSettings from '../components/AdminSettings';
import AdminDeniedTagsPage from './AdminDeniedTagsPage';

export default function AdminSettingsPage() {
  const [activeTab, setActiveTab] = useState('site');
  const [footerText, setFooterText] = useState('');
  const [siteTitle, setSiteTitle] = useState('');
  const [footerEnabled, setFooterEnabled] = useState(true);
  const [titleEnabled, setTitleEnabled] = useState(true);
  const [freeTier, setFreeTier] = useState('');
  const [freeEnabled, setFreeEnabled] = useState(true);
  const [searchClipLimit, setSearchClipLimit] = useState(1000);
  const [taggingMaxPerClip, setTaggingMaxPerClip] = useState(5);
  const [footerLinksTermsOfService, setFooterLinksTermsOfService] = useState(true);
  const [footerLinksPrivacyPolicy, setFooterLinksPrivacyPolicy] = useState(true);
  const [footerLinksContact, setFooterLinksContact] = useState(true);
  const [contactShowForm, setContactShowForm] = useState(true);
  const [contactShowEmail, setContactShowEmail] = useState(true);
  const [contactEmail, setContactEmail] = useState('support@clipto.tv');
  const [landingNumber, setLandingNumber] = useState(true);
  const [publicProfileEnabled, setPublicProfileEnabled] = useState(true);
  const [tiers, setTiers] = useState([]);
  const [badges, setBadges] = useState([]);
  const [badgeEdits, setBadgeEdits] = useState({});
  const [badgeMetaEdits, setBadgeMetaEdits] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [modalOpenId, setModalOpenId] = useState(null);
  const [modalFields, setModalFields] = useState({});
  const [modalSaving, setModalSaving] = useState(false);

  const resolveIconUrl = (iconUrl) => {
    if (!iconUrl) return '/assets/badges/default.png';
    // absolute URLs are returned as-is
    if (/^https?:\/\//i.test(iconUrl)) return iconUrl;
    // stored relative paths like 'badges/clipper.png' should resolve to '/badges/clipper.png'
    return iconUrl.startsWith('/') ? iconUrl : `/${iconUrl}`;
  };
  

  useEffect(() => {
    let mounted = true;
    async function load() {
      try {
        setLoading(true);
        const results = await Promise.allSettled([
          fetchSiteSettings(),
          fetchSiteTitle(),
          fetchAdminSettings(),
          fetchTiers(),
          fetchSiteFreeTier(),
          fetchContactSettings(),
        ]);
        /* c8 ignore start */
        if (mounted) {
        /* c8 ignore stop */
          // If core site calls failed, propagate error so tests expecting load failures work
          if (results[0].status === 'rejected' || results[1].status === 'rejected') {
            /* c8 ignore start */
            const reason = (results[0].status === 'rejected' && results[0].reason) || (results[1].status === 'rejected' && results[1].reason) || new Error('load failed');
            /* c8 ignore stop */
            throw reason;
          }
          /* c8 ignore start */
          const footerRes = results[0].status === 'fulfilled' ? results[0].value : {};
          const titleRes = results[1].status === 'fulfilled' ? results[1].value : {};
          const adminSettingsRes = results[2].status === 'fulfilled' ? results[2].value : {};
          const tiersRes = results[3].status === 'fulfilled' ? results[3].value : [];
          /* c8 ignore stop */
            // load badges as separate request to avoid blocking other core loads
            try {
              const b = await fetchBadges();
              /* c8 ignore start */
              setBadges(b.badges || []);
              /* c8 ignore stop */
            } catch (e) {
              // ignore badge load failures
            }
          /* c8 ignore start */
          const freeRes = results[4].status === 'fulfilled' ? results[4].value : {};
          const contactRes = results[5].status === 'fulfilled' ? results[5].value : {};
          setFooterText(footerRes.text || 'Clipto. Not affiliated with Twitch.');
          setFooterEnabled(typeof footerRes.enabled === 'boolean' ? footerRes.enabled : true);
          setSiteTitle(titleRes.title || 'Clipto');
          setTitleEnabled(typeof titleRes.enabled === 'boolean' ? titleRes.enabled : true);
          const adminData = adminSettingsRes.site || adminSettingsRes || {};
          // free tier value and its feature-flag enabled state come from dedicated endpoint
          setFreeTier((freeRes && freeRes.free_tier) ?? (adminData.free_tier ?? ''));
          setSearchClipLimit(adminData.search_clip_limit ?? 1000);
          setTaggingMaxPerClip(adminData.tagging_max_per_clip ?? 5);
          setFreeEnabled(typeof (freeRes && freeRes.enabled) === 'boolean' ? freeRes.enabled : true);
          /* c8 ignore stop */
          /* c8 ignore start */
          setTiers(Array.isArray(tiersRes) ? tiersRes : (tiersRes.tiers || []));
          /* c8 ignore stop */
          
          // Set footer link visibility settings
          /* c8 ignore start */
          setFooterLinksTermsOfService(typeof footerRes.termsOfService === 'boolean' ? footerRes.termsOfService : true);
          setFooterLinksPrivacyPolicy(typeof footerRes.privacyPolicy === 'boolean' ? footerRes.privacyPolicy : true);
          setFooterLinksContact(typeof footerRes.contact === 'boolean' ? footerRes.contact : true);
          /* c8 ignore stop */

          // Set contact settings
          /* c8 ignore start */
          setContactShowForm(typeof contactRes.show_form === 'boolean' ? contactRes.show_form : true);
          setContactShowEmail(typeof contactRes.show_email === 'boolean' ? contactRes.show_email : true);
          setContactEmail(contactRes.email || 'support@clipto.tv');
          // Site-level toggles (landing number and public profile)
          setLandingNumber(typeof adminData.landing_number === 'boolean' ? adminData.landing_number : true);
          setPublicProfileEnabled(typeof adminData.public_profile === 'boolean' ? adminData.public_profile : true);
          /* c8 ignore stop */

          // no-op: newsletter moved to dedicated admin page
        }
      } catch (e) {
        setError(e.message);
      } finally {
        setLoading(false);
      }
    }
    load();
    return () => { mounted = false; };
  }, []);

  const handleSave = async () => {
    setSaving(true);
    setError(null);
    try {
      // Save based on active tab
      if (activeTab === 'site') {
        // Save title, footer and free tier; tolerate admin update failures so UI still reflects title/footer changes
        const results = await Promise.allSettled([
          updateSiteTitle(siteTitle),
          updateSiteFooter(footerText, footerLinksTermsOfService, footerLinksPrivacyPolicy, footerLinksContact),
          /* c8 ignore start */
          updateAdminSettings({ free_tier: freeTier === '' ? null : Number(freeTier), search_clip_limit: Number(searchClipLimit) || 1000, tagging_max_per_clip: Number(taggingMaxPerClip) || 5 }),
          /* c8 ignore stop */
        ]);

        if (results[0].status === 'fulfilled') setSiteTitle(results[0].value.title);
        if (results[1].status === 'fulfilled') setFooterText(results[1].value.text);
        if (results[0].status === 'rejected' || results[1].status === 'rejected') {
          // propagate core failures
          /* c8 ignore start */
          const reason = (results[0].status === 'rejected' && results[0].reason) || (results[1].status === 'rejected' && results[1].reason) || new Error('save failed');
          /* c8 ignore stop */
          throw reason;
        }
        // ignore admin update failure (results[2])
      /* c8 ignore start */
      } else if (activeTab === 'contact') {
        // Save contact settings
        await updateContactSettings({
          contact_show_form: contactShowForm,
          contact_show_email: contactShowEmail,
          contact_email: contactEmail,
        });
      }
      /* c8 ignore stop */

      // Save site toggles regardless of active tab if present in payload
      if (activeTab === 'site') {
        // include landing_number and public_profile in admin settings update
        try {
          await updateAdminSettings({ landing_number: landingNumber, public_profile: publicProfileEnabled });
        } catch (e) {
          // tolerate failure (other updates already attempted above)
        }
      }
    } catch (e) {
      setError(e.message);
    } finally {
      setSaving(false);
    }
  };

  /* c8 ignore next */
  const _modalThresholdVal = modalFields.threshold ?? '';
  /* c8 ignore next */
  const _modalDescriptionVal = modalFields.description ?? '';
  /* c8 ignore next */
  const _modalIconUrlVal = modalFields.icon_url ?? '';

  return (
    <div className="w-full">
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-white">Settings</h1>
        <p className="text-gray-400 mt-1">Manage site configuration and settings</p>
      </div>

      <div className="bg-dark-800 border border-dark-700 rounded-lg p-6">
        {/* Tab Navigation */}
        <div className="mb-6 border-b border-dark-600">
          <div className="flex space-x-4">
            <button
              onClick={() => setActiveTab('site')}
              className={`px-4 py-2 border-b-2 transition ${
                activeTab === 'site'
                  ? 'border-primary text-primary font-semibold'
                  : 'border-transparent text-gray-400 hover:text-gray-300'
              }`}
            >
              Site Settings
            </button>
            <button
              onClick={() => setActiveTab('contact')}
              className={`px-4 py-2 border-b-2 transition ${
                activeTab === 'contact'
                  ? 'border-primary text-primary font-semibold'
                  : 'border-transparent text-gray-400 hover:text-gray-300'
              }`}
            >
              Contact
            </button>
            <button
              onClick={() => setActiveTab('waitinglist')}
              className={`px-4 py-2 border-b-2 transition ${
                activeTab === 'waitinglist'
                  ? 'border-primary text-primary font-semibold'
                  : 'border-transparent text-gray-400 hover:text-gray-300'
              }`}
            >
              Waiting List
            </button>
            <button
              onClick={() => setActiveTab('badges')}
              className={`px-4 py-2 border-b-2 transition ${
                activeTab === 'badges'
                  ? 'border-primary text-primary font-semibold'
                  : 'border-transparent text-gray-400 hover:text-gray-300'
              }`}
            >
              Badges
            </button>
            <button
              onClick={() => setActiveTab('denied-tags')}
              className={`px-4 py-2 border-b-2 transition ${
                activeTab === 'denied-tags'
                  ? 'border-primary text-primary font-semibold'
                  : 'border-transparent text-gray-400 hover:text-gray-300'
              }`}
            >
              Denied Tags
            </button>
          </div>
        </div>

        {loading ? (
          <p>Loading…</p>
        ) : (
          <div className="space-y-4">
            {/* Site Settings Tab */}
            {activeTab === 'site' && (
              <div>
                <h2 className="text-2xl font-bold mb-4">Site Settings</h2>
                <div className="space-y-4">
                  <div>
                    <div className="flex items-center justify-between">
                      <label className="block text-sm text-gray-300 mb-1">Site title</label>
                      <div className="flex items-center">
                        <label className="flex items-center text-sm text-gray-300">
                          <input type="checkbox" checked={titleEnabled} disabled className="mr-2" />
                          {titleEnabled ? 'Enabled' : 'Disabled'}
                        </label>
                      </div>
                    </div>
                    <input
                      value={siteTitle}
                      onChange={(e) => setSiteTitle(e.target.value)}
                      className="w-full px-3 py-2 bg-dark-800 border border-dark-600 rounded-lg text-white"
                      disabled={!titleEnabled}
                    />
                    <p className="text-xs text-gray-500 mt-1">Site name shown in the page title and metadata.</p>
                  </div>

                  <div>
                    <div className="flex items-center justify-between">
                      <label className="block text-sm text-gray-300 mb-1">Footer text (without © year)</label>
                      <div className="flex items-center">
                        <label className="flex items-center text-sm text-gray-300">
                          <input type="checkbox" checked={footerEnabled} disabled className="mr-2" />
                          {footerEnabled ? 'Enabled' : 'Disabled'}
                        </label>
                      </div>
                    </div>
                    <input
                      value={footerText}
                      onChange={(e) => setFooterText(e.target.value)}
                      className="w-full px-3 py-2 bg-dark-800 border border-dark-600 rounded-lg text-white"
                      disabled={!footerEnabled}
                    />
                    <p className="text-xs text-gray-500 mt-1">This text will appear after the © YEAR prefix in the site footer.</p>

                    <div className="mt-4">
                      <p className="text-sm text-gray-300 mb-2">Footer link visibility:</p>
                      <label className="flex items-center mb-2">
                        <input
                          type="checkbox"
                          checked={footerLinksTermsOfService}
                          onChange={(e) => setFooterLinksTermsOfService(e.target.checked)}
                          className="mr-2"
                          disabled={!footerEnabled}
                        />
                        <span className="text-sm text-gray-300">Terms of Service</span>
                      </label>
                      <label className="flex items-center mb-2">
                        <input
                          type="checkbox"
                          checked={footerLinksPrivacyPolicy}
                          onChange={(e) => setFooterLinksPrivacyPolicy(e.target.checked)}
                          className="mr-2"
                          disabled={!footerEnabled}
                        />
                        <span className="text-sm text-gray-300">Privacy Policy</span>
                      </label>
                      <label className="flex items-center">
                        <input
                          type="checkbox"
                          checked={footerLinksContact}
                          onChange={(e) => setFooterLinksContact(e.target.checked)}
                          className="mr-2"
                          disabled={!footerEnabled}
                        />
                        <span className="text-sm text-gray-300">Contact</span>
                      </label>
                      <p className="text-xs text-gray-500 mt-2">Toggle which footer links are displayed.</p>
                    </div>
                  </div>

                  <div>
                    <label htmlFor="searchClipLimit" className="block text-sm text-gray-300 mb-1">Search clip limit</label>
                    <input
                      id="searchClipLimit"
                      type="number"
                      value={searchClipLimit}
                      onChange={(e) => setSearchClipLimit(e.target.value)}
                      className="w-full px-3 py-2 bg-dark-800 border border-dark-600 rounded-lg text-white"
                    />
                    <p className="text-xs text-gray-500 mt-1">Maximum clips fetched on initial search (default 1000)</p>
                  </div>

                  <div>
                    <label htmlFor="taggingMaxPerClip" className="block text-sm text-gray-300 mb-1">Max tags per clip</label>
                    <input
                      id="taggingMaxPerClip"
                      type="number"
                      value={taggingMaxPerClip}
                      onChange={(e) => setTaggingMaxPerClip(e.target.value)}
                      className="w-full px-3 py-2 bg-dark-800 border border-dark-600 rounded-lg text-white"
                    />
                    <p className="text-xs text-gray-500 mt-1">Maximum tags a clip can have in collections (default 5)</p>
                  </div>

                  <div>
                    <label className="flex items-center">
                      <input
                        type="checkbox"
                        checked={landingNumber}
                        onChange={(e) => setLandingNumber(e.target.checked)}
                        className="mr-2"
                      />
                      <span className="text-sm text-gray-300">Show "By the Numbers" block on landing page</span>
                    </label>
                    <p className="text-xs text-gray-500 mt-1">Toggle the public landing page statistics block.</p>
                  </div>

                  <div>
                    <label className="flex items-center">
                      <input
                        type="checkbox"
                        checked={publicProfileEnabled}
                        onChange={(e) => setPublicProfileEnabled(e.target.checked)}
                        className="mr-2"
                      />
                      <span className="text-sm text-gray-300">Enable public user profiles</span>
                    </label>
                    <p className="text-xs text-gray-500 mt-1">When disabled, public profile pages will return Not Found.</p>
                  </div>

                  <div>
                    <div className="flex items-center justify-between">
                      <label className="block flex-1 text-sm text-gray-300 mb-1">Free Tier</label>
                      <div className="flex items-center">
                        <label className="flex items-center text-sm text-gray-300">
                          <input type="checkbox" checked={freeEnabled} disabled className="mr-2" />
                          {freeEnabled ? 'Enabled' : 'Disabled'}
                        </label>
                      </div>
                    </div>
                    <select
                      value={freeTier}
                      onChange={(e) => setFreeTier(e.target.value)}
                      className="w-full px-3 py-2 bg-dark-800 border border-dark-600 rounded-lg text-white"
                      disabled={!freeEnabled}
                    >
                      <option value="">— Select —</option>
                      {tiers.map((t) => {
                        /* c8 ignore start */
                        const tid = String(t.id ?? t._id ?? t.key);
                        const tname = t.name || t.title || tid;
                        /* c8 ignore stop */
                        return <option key={tid} value={tid}>{tname}</option>;
                      })}
                    </select>
                    <p className="text-xs text-gray-500 mt-1">Select the free tier used for new users and ordering.</p>
                  </div>
                  {error && <div className="text-red-400">{error}</div>}
                  <div>
                    <button
                      onClick={handleSave}
                      disabled={saving}
                      className="px-4 py-2 bg-primary rounded-lg text-white"
                    >
                      {saving ? 'Saving…' : 'Save'}
                    </button>
                  </div>
                </div>
              </div>
            )}

            {/* Denied Tags Tab (rendered at top-level of tabs) */}
            {activeTab === 'denied-tags' && (
              <div>
                <AdminDeniedTagsPage embedded />
              </div>
            )}

            {/* Contact Settings Tab */}
            {activeTab === 'contact' && (
              <div>
                <h2 className="text-2xl font-bold mb-4">Contact Settings</h2>
                <div className="space-y-4">
                  <div>
                    <label className="flex items-center">
                      <input
                        type="checkbox"
                        checked={contactShowForm}
                        onChange={(e) => setContactShowForm(e.target.checked)}
                        className="mr-2"
                      />
                      <span className="text-sm text-gray-300">Show contact form on Contact page</span>
                    </label>
                    <p className="text-xs text-gray-500 mt-1">Allow visitors to submit contact messages.</p>
                  </div>

                  <div>
                    <label className="flex items-center">
                      <input
                        type="checkbox"
                        checked={contactShowEmail}
                        onChange={(e) => setContactShowEmail(e.target.checked)}
                        className="mr-2"
                      />
                      <span className="text-sm text-gray-300">Show contact email address on Contact page</span>
                    </label>
                    <p className="text-xs text-gray-500 mt-1">Display the email address for direct contact.</p>
                  </div>

                  <div>
                    <label className="block text-sm text-gray-300 mb-1">Support email address</label>
                    <input
                      type="email"
                      value={contactEmail}
                      onChange={(e) => setContactEmail(e.target.value)}
                      className="w-full px-3 py-2 bg-dark-800 border border-dark-600 rounded-lg text-white"
                    />
                    <p className="text-xs text-gray-500 mt-1">Where contact form messages will be sent.</p>
                  </div>

                  {error && <div className="text-red-400">{error}</div>}

                  <div>
                    <button
                      onClick={handleSave}
                      disabled={saving}
                      className="px-4 py-2 bg-primary rounded-lg text-white"
                    >
                      {saving ? 'Saving…' : 'Save'}
                    </button>
                  </div>

                  {/* Newsletter moved to its own Admin page */}
                </div>
              </div>
            )}

            {/* Badges Settings Tab */}
            {activeTab === 'badges' && (
              <div>
                <h2 className="text-2xl font-bold mb-4">Badges</h2>
                <div className="space-y-4">
                  {badges.length === 0 && <p className="text-sm text-gray-400">No badge definitions found.</p>}
                  {badges.map((b) => (
                    <div key={b.id} className="bg-dark-800 rounded-lg p-4 border border-dark-700">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center">
                          <img src={resolveIconUrl(b.icon_url)} alt={b.name} className="w-14 h-14 mr-4 rounded" />
                          <div>
                            <div className="text-white font-semibold">{b.name} <span className="text-gray-400 text-sm">({b.key})</span></div>
                            <div className="text-gray-400 text-sm">{b.description}</div>
                            <div className="text-xs text-gray-500 mt-1">Icon: {b.icon_url || '—'}</div>
                          </div>
                        </div>

                        <div className="flex items-center space-x-2">
                          <button
                            onClick={() => {
                              setModalOpenId(b.id);
                              setModalFields({ threshold: b.threshold ?? '', description: b.description ?? '', icon_url: b.icon_url ?? '' });
                            }}
                            className="px-3 py-1 bg-dark-700 border border-dark-600 rounded text-white"
                          >
                            Edit
                          </button>
                        </div>
                      </div>
                    </div>
                  ))}

                  <div>
                    <button
                      onClick={async () => {
                        setSaving(true);
                        setError(null);
                        try {
                          // Collect all badge ids that have edits
                          const ids = new Set([...Object.keys(badgeEdits), ...Object.keys(badgeMetaEdits)]);
                          /* c8 ignore start */
                          const updates = Array.from(ids).map(async (id) => {
                            const payload = {};
                            if (badgeEdits[id] !== undefined) payload.threshold = badgeEdits[id];
                            const meta = badgeMetaEdits[id] || {};
                            if (meta.description !== undefined) payload.description = meta.description;
                            if (meta.icon_url !== undefined) payload.icon_url = meta.icon_url;
                            if (Object.keys(payload).length === 0) return null;
                            const r = await updateBadgeDefinition(id, payload);
                            return r.badge;
                          });
                          /* c8 ignore stop */
                          const res = await Promise.all(updates);
                          // refresh local list
                          /* c8 ignore start */
                          const fresh = await fetchBadges();
                          setBadges(fresh.badges || []);
                          setBadgeEdits({});
                          setBadgeMetaEdits({});
                        } catch (e) {
                          setError(e.message || 'Failed to save badges');
                        } finally {
                          setSaving(false);
                        }
                        /* c8 ignore stop */
                      }}
                      disabled={saving}
                      className="px-4 py-2 bg-primary rounded-lg text-white"
                    >
                      {saving ? 'Saving…' : 'Save Badges'}
                    </button>
                  </div>
                </div>
              </div>
            )}

            {/* Edit Modal */}
            {modalOpenId && (
              <div className="fixed inset-0 z-50 flex items-center justify-center">
                <div className="absolute inset-0 bg-black opacity-60" onClick={() => setModalOpenId(null)} />
                <div className="relative bg-dark-900 rounded-lg p-6 w-full max-w-lg z-10">
                  <h3 className="text-lg font-semibold mb-4">Edit Badge</h3>
                  <div className="space-y-3">
                    <div>
                      <label className="block text-xs text-gray-300 mb-1">Threshold</label>
                      <input type="number" value={_modalThresholdVal} onChange={(e) => setModalFields((s) => ({ ...s, threshold: e.target.value }))} className="w-full px-3 py-2 bg-dark-800 border border-dark-600 rounded text-white" />
                    </div>
                    <div>
                      <label className="block text-xs text-gray-300 mb-1">Description</label>
                      <input type="text" value={_modalDescriptionVal} onChange={(e) => setModalFields((s) => ({ ...s, description: e.target.value }))} className="w-full px-3 py-2 bg-dark-800 border border-dark-600 rounded text-white" />
                    </div>
                    <div>
                      <label className="block text-xs text-gray-300 mb-1">Icon URL</label>
                      <input type="text" value={_modalIconUrlVal} onChange={(e) => setModalFields((s) => ({ ...s, icon_url: e.target.value }))} className="w-full px-3 py-2 bg-dark-800 border border-dark-600 rounded text-white" />
                      <p className="text-xs text-gray-500 mt-1">Relative paths like <span className="text-white">badges/clipper.png</span> are recommended.</p>
                    </div>
                  </div>
                  <div className="mt-4 flex justify-end space-x-2">
                    <button onClick={() => setModalOpenId(null)} className="px-3 py-2 bg-dark-700 border border-dark-600 rounded text-white">Cancel</button>
                    <button
                      onClick={async () => {
                        try {
                          setModalSaving(true);
                          /* c8 ignore start */
                          const payload = {};
                          if (modalFields.threshold !== undefined) payload.threshold = Number(modalFields.threshold) || 0;
                          if (modalFields.description !== undefined) payload.description = modalFields.description;
                          if (modalFields.icon_url !== undefined) payload.icon_url = modalFields.icon_url;
                          await updateBadgeDefinition(modalOpenId, payload);
                          const fresh = await fetchBadges();
                          setBadges(fresh.badges || []);
                          setModalOpenId(null);
                        } catch (e) {
                          setError(e.message || 'Failed to save badge');
                        } finally {
                          setModalSaving(false);
                        }
                        /* c8 ignore stop */
                      }}
                      disabled={modalSaving}
                      className="px-3 py-2 bg-primary rounded text-white"
                    >
                      {modalSaving ? 'Saving…' : 'Save'}
                    </button>
                  </div>
                </div>
              </div>
            )}

            {/* Waiting List Settings Tab */}
            {activeTab === 'waitinglist' && (
              <div>
                <h2 className="text-2xl font-bold mb-4">Waiting List Settings</h2>
                <AdminSettings showHeader={false} freeTier={freeTier} tiers={tiers} />
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
